import { State, useHookstate } from '@hookstate/core'
import { Col, Row } from 'antd'
import Progress from 'antd/es/progress'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ACTION_TYPE_BY_ITEM_STATUS, GET_STATUS_BAR_COLOR_BY_STATE, ITEM_STATES, JobItem } from '../../../domain/items'

const StatusBarContainer = styled.div<{ isLastItem: boolean; totalItems: number; itemQuantity: number }>`
  padding: 0 0.1rem;
  flex: ${({ isLastItem, totalItems, itemQuantity }) =>
    isLastItem ? '1' : `0 0 ${Math.round((itemQuantity / totalItems) * 100)}%`};
  & > div {
    margin-top: 1.4rem;
    & > div {
      & > div:nth-child(1) {
        color: #00000073;
        font-size: 1rem;
        font-weight: 500;
        line-height: 1rem;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }
  }
`

type ItemStatus = {
  [key: string]: number
}

type ItemStatusBarProps = {
  items: State<JobItem[]>
  showStatusLabel?: boolean
  itemStatusTitle?: State<string>
}

const ItemStatusBar = (props: ItemStatusBarProps) => {
  const { items, showStatusLabel, itemStatusTitle } = props
  const { t } = useTranslation()
  const itemStatus = useHookstate<ItemStatus>({} as ItemStatus)

  useEffect(() => {
    if (items.get({ noproxy: true }).length > 0) {
      calculateItemsByStatus(JSON.parse(JSON.stringify(items.get())))
    }
  }, [items])

  useEffect(() => {
    const totalItemPending = itemStatus.get()[ITEM_STATES.PENDING]
    const totalItemAdded = itemStatus.get()[ITEM_STATES.ADDED]
    const totalItemRemoved = itemStatus.get()[ITEM_STATES.REMOVED]
    const totalItemRejected = itemStatus.get()[ITEM_STATES.REJECTED]

    if (!totalItemPending) {
      itemStatusTitle.set(t<string>('itemChangesFinished'))
    }

    if (totalItemPending && (totalItemAdded || totalItemRemoved || totalItemRejected)) {
      itemStatusTitle.set(t<string>('itemChangesInProcess'))
    }
  }, [itemStatus.get()])

  const getItemsQuantityByStatus = (items: JobItem[], status: string[]): number => {
    return items.filter((item) => status.includes(item.state)).length
  }

  const calculateItemsByStatus = (itemList: JobItem[]): void => {
    let itemStatusGrupped: ItemStatus = {}

    if (getItemsQuantityByStatus(itemList, [ITEM_STATES.PENDING]) > 0) {
      itemStatusGrupped[ITEM_STATES.PENDING] = getItemsQuantityByStatus(itemList, [ITEM_STATES.PENDING])
    }

    if (getItemsQuantityByStatus(itemList, [ITEM_STATES.ADDED]) > 0) {
      itemStatusGrupped[ITEM_STATES.ADDED] = getItemsQuantityByStatus(itemList, [ITEM_STATES.ADDED])
    }

    if (getItemsQuantityByStatus(itemList, [ITEM_STATES.REMOVED, ITEM_STATES.CANCELLED]) > 0) {
      itemStatusGrupped[ITEM_STATES.REMOVED] = getItemsQuantityByStatus(itemList, [
        ITEM_STATES.REMOVED,
        ITEM_STATES.CANCELLED
      ])
    }

    if (
      getItemsQuantityByStatus(itemList, [
        ACTION_TYPE_BY_ITEM_STATUS.REJECTED_BY_USER,
        ACTION_TYPE_BY_ITEM_STATUS.REJECTED_BY_DRIVER
      ]) > 0
    ) {
      itemStatusGrupped[ITEM_STATES.REJECTED] = getItemsQuantityByStatus(itemList, [
        ACTION_TYPE_BY_ITEM_STATUS.REJECTED_BY_USER,
        ACTION_TYPE_BY_ITEM_STATUS.REJECTED_BY_DRIVER
      ])
    }

    itemStatus.set(itemStatusGrupped)
  }

  return (
    <Row gutter={4}>
      {Object.keys(itemStatus.get()).map((statusKey, index) => (
        <StatusBarContainer
          isLastItem={index === Object.entries(itemStatus.get()).length - 1}
          totalItems={items.get().length}
          itemQuantity={itemStatus.get()[statusKey]}
          key={statusKey}
        >
          <Row>
            <Col span={24}>
              {showStatusLabel && (
                <div>
                  {itemStatus.get()[statusKey]} {t<string>(`jobs.job.info.items-view.${statusKey.toLowerCase()}`)}
                </div>
              )}
              <Progress percent={100} showInfo={false} strokeColor={GET_STATUS_BAR_COLOR_BY_STATE[statusKey]} />
            </Col>
          </Row>
        </StatusBarContainer>
      ))}
    </Row>
  )
}

export { ItemStatusBar }
